import { Button, Divider, Group, Loader, Menu, Stack, Switch, Text, Textarea, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconClipboardText, IconExternalLink, IconTrash } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useLocation, useMatchRoute } from "@tanstack/react-router";
import { zodResolver } from "mantine-form-zod-resolver";
import { Suspense, useEffect, useState } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { FloatingActionButton } from "shared/components/global/FloatingActionButton/FloatingActionButton";
import { useArchiveCert } from "shared/components/ipaws/certificate/useArchiveCert";
import { formatIpawsChannel } from "shared/components/ipaws/format";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { usePermissionCheck } from "shared/stores/oidc";
import { useAside } from "shared/utils/aside";
import { notify } from "shared/utils/notify";
import * as zx from "shared/utils/zod";
import { graphql } from "src/gql";
import { IpawsChannel, Permission } from "src/gql/graphql";
import { queryClient } from "src/queryClient";
import z from "zod";

const certEditFormSchema = z.object({
    id: zx.nonemptyUUID(),
    name: zx.nonemptyString(),
    description: zx.nonemptyString(),
    enabled: z.boolean(),
    channels: z.object({ channel: z.nativeEnum(IpawsChannel), userEnabled: z.boolean() }).array(),
});

type CertEditFormSchema = z.infer<typeof certEditFormSchema>;

const CertDetails = ({ certId }: { certId: string }) => {
    const nav = Route.useNavigate();
    const canAssignRoles = usePermissionCheck(Permission.CustomerRolesManage);
    const [loading, setLoading] = useState(false);

    const cert = useSuspenseQuery(queries.certDetails(certId));
    const certData = cert.data.ipawsCertificate;
    const updateCert = useGraphqlMutation(muts.updateCert);
    const { archiveCert } = useArchiveCert(certId, () => {
        queryClient
            .invalidateQueries({ queryKey: ["ipaws", "certifications", "table"] })
            .then(() => nav({ to: "/ipaws/certificates" }))
            .catch(notify.catch);
    });

    const form = useForm<CertEditFormSchema>({
        mode: "controlled",
        initialValues: certData,
        validate: zodResolver(certEditFormSchema),
    });

    useEffect(() => {
        const vals = form.getValues();
        if (certData.id != vals.id) {
            form.setInitialValues(certData);
            form.reset();
        }
    }, [certData.id]);

    return (
        <form
            onSubmit={form.onSubmit((vals) => {
                setLoading(true);
                const { id: _, ...updateVals } = vals;
                updateCert
                    .mutateAsync({
                        id: certId,
                        params: {
                            ...updateVals,
                            channels: updateVals.channels.map((each) =>
                                [IpawsChannel.Public, IpawsChannel.Capexch].includes(each.channel) ? { ...each, userEnabled: true } : each,
                            ),
                            boundaries: certData.boundaries,
                        },
                    })
                    .then(() => {
                        form.setInitialValues(vals);
                        form.reset();
                    })
                    .then(() => queryClient.invalidateQueries({ queryKey: ["ipaws", "certifications"] }))
                    .then(() => notify.show.success("Updated certification"))
                    .finally(() => setLoading(false))
                    .catch(notify.catch);
            })}
        >
            <Stack>
                <Link to="/ipaws/certificates/$certId/details" params={{ certId }}>
                    <Button
                        variant="default"
                        leftSection={<IconClipboardText size={16} />}
                        mx="md"
                        w="calc(100% - 2 * var(--mantine-spacing-md))"
                    >
                        View Certificate Details
                    </Button>
                </Link>
                <TextInput label="Name" required placeholder="Enter name..." {...form.getInputProps("name")} />
                <Textarea label="Description" required placeholder="Enter description..." {...form.getInputProps("description")} />
                <Switch
                    label={<Text fw={600}>Enabled</Text>}
                    {...form.getInputProps("enabled", { type: "checkbox" })}
                    onChange={(e) => {
                        e.preventDefault();
                        const { checked } = e.target;
                        void notify
                            .confirm(`Are you sure you would like to ${checked ? "enable" : "disable"} this certificate?`)
                            .then(({ close }) => {
                                form.setFieldValue("enabled", checked);
                                close();
                            });
                    }}
                />
                {canAssignRoles && (
                    <Link to="/organization/users">
                        <Button variant="default" rightSection={<IconExternalLink size={16} />}>
                            Assign Roles
                        </Button>
                    </Link>
                )}
                <Stack gap="xs">
                    <Title order={4} size="h6">
                        Channels
                    </Title>
                    <Text fz="sm">Disable channels to send to within this certificate.</Text>
                </Stack>
                {certData.channels
                    .filter(({ channel }) => ![IpawsChannel.Public, IpawsChannel.Capexch].includes(channel))
                    .toSorted((a, b) => a.channel.localeCompare(b.channel))
                    .map((channel) => {
                        const formChannels = form.getValues().channels;
                        const formChannel = formChannels.find((c) => c.channel == channel.channel);
                        if (!formChannel) throw new Error("Cannot find channel with id " + channel.channel);
                        return (
                            <Switch
                                key={channel.channel}
                                label={<Text fw={600}>{formatIpawsChannel(channel.channel)}</Text>}
                                checked={formChannel.userEnabled}
                                onChange={(e) => {
                                    form.setFieldValue(
                                        "channels",
                                        formChannels.map((c) =>
                                            c.channel == channel.channel ? { ...formChannel, userEnabled: e.currentTarget.checked } : c,
                                        ),
                                    );
                                }}
                            />
                        );
                    })}
                <Group justify="flex-end">
                    <Button
                        variant="default"
                        onClick={() => {
                            form.reset();
                            nav({ to: "/ipaws/certificates" }).catch(notify.catch);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button type="submit" disabled={!form.isDirty()} loading={loading}>
                        Save
                    </Button>
                </Group>
                <Divider />
                <Button leftSection={<IconTrash size={16} />} onClick={() => void archiveCert()}>
                    Archive
                </Button>
            </Stack>
        </form>
    );
};

function Page() {
    const aside = useAside();
    const nav = Route.useNavigate();
    const { certId } = Route.useParams();
    const { href } = useLocation();
    const matchRoute = useMatchRoute();
    const hasIpawsCreate = usePermissionCheck(Permission.IpawsAlertModify);

    useEffect(() => {
        if (
            !matchRoute({ to: "/ipaws/certificates" }) ||
            matchRoute({ to: "/ipaws/certificates/create" }) ||
            matchRoute({ to: "/ipaws/certificates/$certId/details" })
        )
            aside.dismiss({ silent: true });
    }, [href]);

    useEffect(() => {
        aside.present({
            label: "Edit Certificate",
            component: (
                <Suspense fallback={<Loader />}>
                    <CertDetails key={certId} certId={certId} />
                </Suspense>
            ),
            sticky: true,
            didDismiss: () => nav({ to: "/ipaws/certificates" }),
        });
    }, [certId]);

    return hasIpawsCreate ? (
        <FloatingActionButton>
            <Menu.Item component={Link} to="/ipaws/alerts/new">
                New IPAWS Alert
            </Menu.Item>
        </FloatingActionButton>
    ) : null;
}

/**
 * @public
 */
export const Route = createFileRoute("/_auth/ipaws/certificates/$certId/")({
    loader: ({ params: { certId } }) => {
        queryClient.ensureQueryData(queries.certDetails(certId)).catch(notify.catch);
    },
    component: () => (
        <Suspense fallback={<Loader />}>
            <Page />
        </Suspense>
    ),
});

const queries = {
    certDetails: (id: string) =>
        queryOptions({
            queryKey: ["ipaws", "certifications", "edit", id],
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query IpawsCertificateEditPanel($id: UUID!) {
                            ipawsCertificate(id: $id) {
                                id
                                name
                                description
                                enabled
                                channels {
                                    channel
                                    userEnabled
                                }
                                boundaries {
                                    name
                                    polygon
                                }
                            }
                        }
                    `),
                    { id },
                ),
        }),
};

const muts = {
    updateCert: graphql(`
        mutation IpawsCertificateUpdate($id: UUID!, $params: UpdateIpawsCertInfoInput!) {
            updateIpawsCertificate(id: $id, params: $params) {
                id
            }
        }
    `),
};
